import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, User, AlertCircle } from 'lucide-react';
import { useAuth } from '@features/auth/context/AuthContext';
import { ThemeToggle } from '@shared/components/ThemeToggle';
import { APP_VERSION } from '@/version';

export const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const { login } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!username || !password) {
            setError('Veuillez remplir tous les champs');
            return;
        }

        const success = await login(username, password);
        if (!success) {
            setError('Identifiants incorrects');
        }
    };


    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-300">
            {/* Animated Background */}
            <div className="absolute inset-0 overflow-hidden -z-10">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-blob" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-blob animation-delay-2000" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-blob animation-delay-4000" />
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                {/* Main Card */}
                <div className="glass-panel p-8 md:p-10 shadow-2xl border-white/10">
                    {/* Header */}
                    <div className="text-center mb-10">
                        <motion.img
                            src="/logo.png"
                            alt="Vigie Logo"
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="w-24 h-24 mb-6 shadow-2xl shadow-primary/20 rounded-[2rem] mx-auto"
                        />
                        <motion.h1
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="text-3xl font-bold mb-2 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent"
                        >
                            Vigie
                        </motion.h1>
                        <motion.p
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="text-sm font-medium opacity-60"
                        >
                            L'Intelligence Opérationnelle
                        </motion.p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Username */}
                        <div className="space-y-2">
                            <label className="text-xs font-semibold opacity-50 uppercase tracking-wider ml-1">
                                Identifiant
                            </label>
                            <div className="relative group">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 opacity-40 group-focus-within:opacity-100 group-focus-within:text-primary transition-all" size={20} />
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="glass-input !pl-12 w-full text-lg placeholder:opacity-30 focus:placeholder:opacity-50"
                                    placeholder="Ex: admin"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <label className="text-xs font-semibold opacity-50 uppercase tracking-wider ml-1">
                                Mot de passe
                            </label>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 opacity-40 group-focus-within:opacity-100 group-focus-within:text-primary transition-all" size={20} />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="glass-input !pl-12 w-full text-lg placeholder:opacity-30 focus:placeholder:opacity-50"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm"
                            >
                                <AlertCircle size={18} className="shrink-0" />
                                <span className="font-medium">{error}</span>
                            </motion.div>
                        )}

                        {/* Submit Button */}
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            className="w-full glass-button bg-gradient-to-r from-primary/80 to-accent/80 hover:from-primary hover:to-accent border-none text-white shadow-lg shadow-primary/25 text-lg font-semibold py-4"
                        >
                            Se connecter
                        </motion.button>
                    </form>


                </div>
                {/* Footer and Toggle */}
                <div className="mt-8 flex flex-col items-center justify-center gap-4">

                    <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-3 text-xs font-medium text-gray-400">
                            <span>VIGIE © 2026</span>
                            <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                            <span className="font-mono opacity-70">v{APP_VERSION}</span>
                        </div>

                        <a
                            href="https://ma-dsi.info"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 hover:text-accent transition-colors"
                        >
                            <span>Une solution</span>
                            <span className="font-bold text-gray-400 group-hover:text-white transition-colors border-b border-transparent group-hover:border-accent">ma-dsi.info</span>
                        </a>
                    </div>

                    <div className="opacity-50 hover:opacity-100 transition-opacity">
                        <ThemeToggle />
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

